import React, { useState, useEffect } from 'react';
import { db, storage } from '../firebaseConfig';
import {
  collection,
  addDoc,
  getDocs,
  doc,
  serverTimestamp,
  getDoc,
  updateDoc,
  deleteDoc,
  query,
  where
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { v4 as uuidv4 } from 'uuid';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './ManageTombola.css';

const ManageTombola = () => {
  const { t } = useTranslation();
  const [year, setYear] = useState(new Date().getFullYear());
  const [awards, setAwards] = useState([{ description: '', image: null }]);
  const [minPoints, setMinPoints] = useState(0);
  const [tombolas, setTombolas] = useState([]);
  const [message, setMessage] = useState('');
  const coffeeShopId = localStorage.getItem('coffee_shop_id');
  const navigate = useNavigate();

  useEffect(() => {
    if (!coffeeShopId) return;

    const fetchTombolas = async () => {
      const q = query(collection(db, 'tombolas'), where('coffee_shop_id', '==', coffeeShopId));
      const tombolaSnapshot = await getDocs(q);

      const tombolasData = await Promise.all(
        tombolaSnapshot.docs.map(async (docSnap) => {
          const tombola = docSnap.data();
          const participantsWithNames = await Promise.all(
            (tombola.participants || []).map(async ({ token }) => {
              const customerQuery = query(collection(db, 'customers'), where('token', '==', token));
              const customerSnapshot = await getDocs(customerQuery);
              return customerSnapshot.empty
                ? { name: t('unknown_customer'), token }
                : customerSnapshot.docs[0].data();
            })
          );
          return { id: docSnap.id, ...tombola, participantNames: participantsWithNames };
        })
      );

      setTombolas(tombolasData);
    };

    fetchTombolas();
  }, [coffeeShopId, t]);

  useEffect(() => {
    const header = document.querySelector('.tombola-header');
    const scrollable = document.querySelector('.tombola-scrollable');
    const updatePadding = () => {
      const headerHeight = header.offsetHeight;
      scrollable.style.paddingTop = `${headerHeight + 20}px`;
      scrollable.style.height = `calc(100dvh - ${headerHeight + 20}px)`;
    };
    updatePadding();
    window.addEventListener('resize', updatePadding);
    return () => window.removeEventListener('resize', updatePadding);
  }, []);

  const handleAwardChange = (index, field, value) => {
    const updatedAwards = [...awards];
    updatedAwards[index][field] = value;
    setAwards(updatedAwards);
  };

  const addAwardField = () => {
    setAwards([...awards, { description: '', image: null }]);
  };

  const uploadImageAndGetUrl = async (image) => {
    const storageRef = ref(storage, `tombola_prizes/${uuidv4()}`);
    await uploadBytes(storageRef, image);
    return getDownloadURL(storageRef);
  };

  const submitTombola = async () => {
    if (!coffeeShopId) return setMessage(t('tombola_missing_coffee_shop'));

    try {
      const awardsWithUrls = await Promise.all(
        awards.map(async (award) => ({
          description: award.description,
          image_url: award.image ? await uploadImageAndGetUrl(award.image) : null,
        }))
      );

      await addDoc(collection(db, 'tombolas'), {
        year,
        coffee_shop_id: coffeeShopId,
        number_of_awards: awards.length,
        awards: awardsWithUrls,
        min_points_required: minPoints,
        participants: [],
        winners: [],
        extraction_date: null,
        createdAt: serverTimestamp(),
      });

      setMessage(t('tombola_success'));
      setYear(new Date().getFullYear());
      setMinPoints(0);
      setAwards([{ description: '', image: null }]);
    } catch (error) {
      setMessage(`${t('error')}: ${error.message}`);
    }
  };

  const extractWinners = async (tombolaId, numberOfWinners) => {
    const tombolaRef = doc(db, 'tombolas', tombolaId);
    const snapshot = await getDoc(tombolaRef);
    if (!snapshot.exists()) return alert(t('tombola_not_found'));

    const { participants } = snapshot.data();
    if (participants.length < numberOfWinners) return alert(t('tombola_not_enough_participants'));

    const shuffled = participants.sort(() => 0.5 - Math.random());
    const winners = shuffled.slice(0, numberOfWinners);

    await updateDoc(tombolaRef, { winners, extraction_date: new Date() });

    alert(t('tombola_extracted_success'));
  };

  const deleteTombola = async (id) => {
    await deleteDoc(doc(db, 'tombolas', id));
    setMessage(t('tombola_deleted'));
  };

  return (
    <div className="manage-tombola-container">
      <h2 className="tombola-header">🎖️ {t('manage_tombola')}</h2>

      <div className="tombola-scrollable">
        <div className="form-group">
          <input className="input-field" type="number" value={year} onChange={(e) => setYear(e.target.value)} placeholder={t('year')} />
          <input className="input-field" type="number" value={minPoints} onChange={(e) => setMinPoints(Number(e.target.value))} placeholder={t('min_points')} />
        </div>

        {awards.map((award, index) => (
          <div className="award-item" key={index}>
            <input
              className="input-field"
              type="text"
              value={award.description}
              onChange={(e) => handleAwardChange(index, 'description', e.target.value)}
              placeholder={`${t('award')} ${index + 1}`}
            />
            <input
              className="file-input"
              type="file"
              onChange={(e) => handleAwardChange(index, 'image', e.target.files[0])}
            />
          </div>
        ))}

        <button className="btn-primary" onClick={addAwardField}>➕ {t('add_award')}</button>
        <button className="btn-success" onClick={submitTombola}>🚀 {t('save_tombola')}</button>
        <button className="btn-secondary" onClick={() => navigate('/dashboard')}>⬅️ {t('dashboard')}</button>

        {message && <div className="notification">{message}</div>}

        <h3 className="tombola-subheader">🎟️ {t('existing_tombolas')}</h3>

        <div className="tombola-cards">
          {tombolas.map((tb) => (
            <div className="tombola-card" key={tb.id}>
              <h4>{tb.year} ({tb.number_of_awards} {t('awards_label')})</h4>
              <div className="tombola-buttons">
                <button className="btn-primary" onClick={() => extractWinners(tb.id, tb.number_of_awards)}>🎉 {t('extract_winners')}</button>
                <button className="btn-danger" onClick={() => deleteTombola(tb.id)}>🗑️ {t('delete_tombola')}</button>
              </div>

              <strong>{t('participants')}:</strong>
              <ul className="participants-list">
                {tb.participantNames.length > 0 ? (
                  tb.participantNames.map((p, idx) => <li key={idx}>{p.name}</li>)
                ) : (
                  <li>{t('no_participants')}</li>
                )}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ManageTombola;
